import { useEffect, useMemo, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import {
    getDashboardV3UserKpi,
    getDashboardV3Users,
} from '../services/dashboardV3.service';
import type {
    DashboardV3UserDataParams,
    DashboardV3UserKpiData,
    DashboardV3UserRow,
} from '../services/dashboardV3.types';

const EMPTY_USER_KPI: DashboardV3UserKpiData = {
    totalUsers: 0,
    totalUsersToday: 0,
    newUsers: 0,
    newUsersDelta: 0,
    activeUsers: 0,
    activeUsersDelta: 0,
    depositUsers: 0,
    depositUsersDelta: 0,
};

export function useDashboardV3UserData(params: DashboardV3UserDataParams) {
    const notify = useNotify();
    const t = useTranslate();

    const [kpi, setKpi] = useState<DashboardV3UserKpiData>(EMPTY_USER_KPI);
    const [list, setList] = useState<DashboardV3UserRow[]>([]);
    const [total, setTotal] = useState(0);

    const [kpiLoading, setKpiLoading] = useState(false);
    const [usersLoading, setUsersLoading] = useState(false);

    const kpiParams = useMemo(
        () => ({
            range: params.range,
            startDate: params.range === 'custom' ? params.startDate : undefined,
            endDate: params.range === 'custom' ? params.endDate : undefined,
        }),
        [params.range, params.startDate, params.endDate]
    );

    // 用户列表的数据与右上角时间范围无关，因此 userParams 不包含 range/startDate/endDate，
    // 切换时间范围只会刷新 KPI，不会重新请求列表。
    const userParams = useMemo(
        () => ({
            page: params.page,
            size: params.size,

            loginMethod: params.loginMethod,
            activeStatus: params.activeStatus,

            userId: params.userId,
            walletAddress: params.walletAddress,
            loginAccount: params.loginAccount,
            parentWalletAddress: params.parentWalletAddress,

            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
        }),
        [
            params.page,
            params.size,
            params.loginMethod,
            params.activeStatus,
            params.userId,
            params.walletAddress,
            params.loginAccount,
            params.parentWalletAddress,
            params.sortBy,
            params.sortOrder,
        ]
    );

    /**
     * 用户页 KPI：受右上角时间范围影响。
     */
    useEffect(() => {
        let alive = true;

        setKpiLoading(true);

        getDashboardV3UserKpi(kpiParams)
            .then((resp) => {
                if (!alive) return;
                setKpi(resp?.data ?? EMPTY_USER_KPI);
            })
            .catch((e: any) => {
                if (!alive) return;
                notify(
                    e?.message ||
                    t('module.dashboardV3.users.messages.kpiLoadFailed', {
                        _: 'Failed to load user KPI data',
                    }),
                    { type: 'warning' }
                );
                setKpi(EMPTY_USER_KPI);
            })
            .finally(() => {
                if (!alive) return;
                setKpiLoading(false);
            });

        return () => {
            alive = false;
        };
    }, [kpiParams, notify, t]);

    /**
     * 用户列表：受时间范围、筛选、排序、分页影响。
     * 后端约定：列表按照注册时间筛选。
     */
    useEffect(() => {
        let alive = true;

        setUsersLoading(true);

        getDashboardV3Users(userParams)
            .then((resp) => {
                if (!alive) return;
                setList(resp?.data?.list ?? []);
                setTotal(Number(resp?.total ?? 0));
            })
            .catch((e: any) => {
                if (!alive) return;
                notify(
                    e?.message ||
                    t('module.dashboardV3.users.messages.loadFailed', {
                        _: 'Failed to load user data',
                    }),
                    { type: 'warning' }
                );
                setList([]);
                setTotal(0);
            })
            .finally(() => {
                if (!alive) return;
                setUsersLoading(false);
            });

        return () => {
            alive = false;
        };
    }, [userParams, notify, t]);

    return {
        kpi,
        list,
        total,

        loading: kpiLoading || usersLoading,
        kpiLoading,
        usersLoading,
    };
}